import { SystemConfig } from "@/configs";
import { t } from "@/i18n";
import { AuthService } from "@/lib/services";
import { clearSiteCreationSkipped, setAuthToken, sleep } from "@/lib/utils";
import React from "react";
import { z } from "zod";

export type LoginStep = "email" | "code";

export function isValidEmail(email: string): boolean {
  const parser = z.email();
  return parser.safeParse(email).success;
}

export interface UseLoginOptions {
  onSuccess?: () => void | Promise<void>;
}

export function useLogin(options: UseLoginOptions = {}) {
  const { onSuccess } = options;
  const [step, setStep] = React.useState<LoginStep>("email");
  const [email, setEmail] = React.useState("");
  const [codeValue, setCodeValue] = React.useState("");
  const [isSending, setIsSending] = React.useState(false);
  const [isVerifying, setIsVerifying] = React.useState(false);
  const [hasVerificationError, setHasVerificationError] = React.useState(false);
  const [emailError, setEmailError] = React.useState("");
  const [codeError, setCodeError] = React.useState("");
  const [turnstileToken, setTurnstileToken] = React.useState("");
  const [turnstileResetKey, setTurnstileResetKey] = React.useState(0);
  const turnstileSiteKey = SystemConfig.turnstileSiteKey;
  const [resendUntil, setResendUntil] = React.useState(0);
  const [resendSeconds, setResendSeconds] = React.useState(0);
  const sendingRef = React.useRef(false);

  const startCooldown = React.useCallback((seconds = 60) => {
    setResendUntil(Date.now() + seconds * 1000);
    setResendSeconds(seconds);
  }, []);

  React.useEffect(() => {
    if (!resendUntil) return;
    const tick = () => setResendSeconds(Math.max(0, Math.ceil((resendUntil - Date.now()) / 1000)));
    tick();
    const timer = window.setInterval(tick, 1000);
    return () => window.clearInterval(timer);
  }, [resendUntil]);

  const canSubmitEmail = isValidEmail(email) && !isSending && resendSeconds === 0 && (!turnstileSiteKey || turnstileToken.length > 0);
  const canResend = canSubmitEmail && !isVerifying;

  const resetTurnstile = React.useCallback(() => {
    setTurnstileToken("");
    setTurnstileResetKey((value) => value + 1);
  }, []);

  const requestLogin = React.useCallback(async () => {
    if (sendingRef.current || isVerifying || Date.now() < resendUntil || !isValidEmail(email)) return;
    if (turnstileSiteKey && !turnstileToken) return;
    clearSiteCreationSkipped();
    sendingRef.current = true;
    setIsSending(true);
    if (step === "code") startCooldown();
    try {
      const response = await AuthService.requestLogin({ requiredAuth: false, email, turnstileToken });
      if (response.error) {
        if (response.code === 429) startCooldown(Math.max(60, response.data?.rateLimit?.retryAfter || 60));
        if (step === "email") setEmailError(response.error);
        else setCodeError(response.error);
      } else {
        startCooldown();
        setCodeError("");
        setCodeValue("");
        setHasVerificationError(false);
        setStep("code");
      }
    } catch {
      const message = t("common.system.somethingWentWrong");
      if (step === "email") setEmailError(message);
      else setCodeError(message);
    } finally {
      resetTurnstile();
      setIsSending(false);
      sendingRef.current = false;
    }
  }, [email, isVerifying, resendUntil, step, startCooldown, resetTurnstile, turnstileSiteKey, turnstileToken]);

  const verifyCode = React.useCallback(
    async (codeToVerify?: string) => {
      const targetCode = codeToVerify ?? codeValue;
      const codeNumber = Number(targetCode);
      if (Number.isNaN(codeNumber) || codeNumber < 100000 || codeNumber > 999999 || isVerifying || sendingRef.current) return;

      setIsVerifying(true);
      setHasVerificationError(false);
      await sleep(0.8);

      const response = await AuthService.verifyLogin({
        requiredAuth: false,
        email,
        code: codeNumber,
      });

      if (response.error) {
        setCodeError(response.error);
        setIsVerifying(false);
        setHasVerificationError(true);
      } else {
        clearSiteCreationSkipped();
        setAuthToken(response.data.token);
        if (onSuccess) {
          try {
            await onSuccess();
          } catch {
          } finally {
            setIsVerifying(false);
          }
        } else {
          setIsVerifying(false);
        }
      }
    },
    [codeValue, email, isVerifying, onSuccess],
  );

  const handleCodeChange = React.useCallback(
    (newCode: string) => {
      setCodeValue(newCode);
      if (codeError) setCodeError("");
      // Auto-verify on 6th digit ONLY if typing fresh for the first time (no active error)
      if (newCode.length === 6 && !hasVerificationError && !isVerifying) {
        verifyCode(newCode);
      }
    },
    [codeError, hasVerificationError, isVerifying, verifyCode],
  );

  const goBackToEmail = React.useCallback(() => {
    setStep("email");
    setIsVerifying(false);
    setCodeValue("");
    setHasVerificationError(false);
    setCodeError("");
    resetTurnstile();
  }, [resetTurnstile]);

  return {
    step,
    email,
    setEmail: (value: string) => {
      setEmail(value);
      if (emailError) setEmailError("");
    },
    emailError,
    codeError,
    codeValue,
    isSending,
    isVerifying,
    hasVerificationError,
    canSubmitEmail,
    canResend,
    resendSeconds,
    turnstileSiteKey,
    turnstileResetKey,
    setTurnstileToken,
    requestLogin,
    verifyCode,
    handleCodeChange,
    goBackToEmail,
  };
}
